import React, { useState } from 'react';
import { Phone, Delete, X, Plus } from 'lucide-react';

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['+', '0', '#'],
];

const KEY_LETTERS = {
  '2': 'ABC', '3': 'DEF', '4': 'GHI', '5': 'JKL',
  '6': 'MNO', '7': 'PQRS', '8': 'TUV', '9': 'WXYZ',
};

export default function DialPad({ onCall, onClose, calling, callerNumber, defaultPrefix = '+34' }) {
  const [number, setNumber] = useState(defaultPrefix);

  const press = (digit) => setNumber(prev => prev + digit);
  const backspace = () => setNumber(prev => prev.length > 0 ? prev.slice(0, -1) : prev);

  const handleCall = () => {
    const cleaned = number.replace(/[\s\-()]/g, '');
    if (cleaned.length >= 5) onCall(cleaned);
  };

  const handleInputChange = (e) => {
    const val = e.target.value.replace(/[^0-9+*#]/g, '');
    setNumber(val);
  };

  const isValidNumber = number.replace(/[\s\-()]/g, '').length >= 5;

  return (
    <div className="flex flex-col items-center" data-testid="dial-pad">
      {/* Header */}
      <div className="flex items-center justify-between w-full mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Realizar llamada
          </h3>
          {callerNumber && (
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Desde: {callerNumber}
            </p>
          )}
        </div>
        <button onClick={onClose} data-testid="dial-pad-close"
          className="w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
          style={{ color: 'var(--text-muted)' }}>
          <X size={16} />
        </button>
      </div>

      {/* Editable number input */}
      <div className="w-full mb-4">
        <input
          type="tel"
          value={number}
          onChange={handleInputChange}
          data-testid="dial-number-display"
          placeholder="+34612345678"
          className="w-full px-4 py-3 rounded-xl text-center text-xl font-mono font-bold tracking-wider outline-none"
          style={{
            backgroundColor: 'var(--surface-secondary)',
            color: number ? 'var(--text-primary)' : 'var(--text-muted)',
            border: '1px solid var(--border-color)',
          }}
        />
        <p className="text-[9px] text-center mt-1" style={{ color: 'var(--text-muted)' }}>
          Incluye prefijo de pais (+34, +1, etc.)
        </p>
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {KEYS.map((row) =>
          row.map((key) => (
            <button
              key={key}
              onClick={() => press(key)}
              data-testid={`dial-key-${key === '+' ? 'plus' : key}`}
              className="w-16 h-16 rounded-2xl flex flex-col items-center justify-center transition-all duration-100 active:scale-95 hover:brightness-125"
              style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-primary)' }}
            >
              <span className="text-lg font-semibold leading-none">{key}</span>
              {KEY_LETTERS[key] && (
                <span className="text-[8px] tracking-[2px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {KEY_LETTERS[key]}
                </span>
              )}
            </button>
          ))
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-4">
        <button
          onClick={backspace}
          disabled={!number}
          data-testid="dial-backspace"
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
          style={{ color: number ? 'var(--text-secondary)' : 'var(--text-muted)' }}
        >
          <Delete size={20} />
        </button>

        <button
          onClick={handleCall}
          disabled={!isValidNumber || calling}
          data-testid="dial-call-btn"
          className="w-16 h-16 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40"
          style={{ backgroundColor: calling ? '#ef4444' : '#22c55e' }}
        >
          <Phone size={24} className={calling ? 'rotate-[135deg]' : ''} />
        </button>

        <div className="w-12" />
      </div>
    </div>
  );
}
